'use server'

import { createClient } from '@/lib/supabase/server'
import { createAdminClient } from '@/lib/supabase/admin'
import { getPhraseTranslation, getRuToTrLookup, getSentenceGloss, getWordTranslation, type RuLookup } from '@/lib/deepseek'
import { lookupCacheKey, normalizeRu, russianGlossMatches } from '@/lib/ru-lookup'
import { cacheKey, consensusOf, glossToCacheRows, hasTurkish, normalizeTr, segmentText, type CachedTranslation, type CacheRow, type TranslateKind } from '@/lib/tr-text'
import { deviceId, getAiUsage, guestCacheIpAllowed, recordAiUsage, usageExhausted } from '@/lib/ai-usage'
import { headers } from 'next/headers'
import type { SupabaseClient } from '@supabase/supabase-js'

// Перевод по наведению/нажатию (.scratch/tap-translate).
// Порядок: словарь (проверенный, мгновенный) → общий кэш → DeepSeek.
// Для слова модель переводит ВСЁ его предложение за один запрос — остальные
// слова этой фразы тут же попадают в кэш. Ответ пишется в translation_cache
// service-role клиентом — ученик сам писать туда не может (см. миграцию
// 20260910140000_translation_cache).

// source = 'limit' — недельный лимит обращений к модели исчерпан (lib/ai-usage.ts):
// translation пустой, notice — текст для попапа. Не исключение: Next в
// проде прячет текст ошибок server action, а сообщение ученику нужно.
// source = 'guest' — гость в уроке без входа (lib/guest-lesson.ts): в словаре и
// кэше перевода нет, а модель только после входа; попап зовёт войти.
export type TranslateResult = CachedTranslation & { source: 'dictionary' | 'cache' | 'ai' | 'limit' | 'guest'; notice?: string }

const LIMIT_NOTICE = (limit: number) =>
  `Лимит переводов AI на эту неделю (${limit}) закончился, новые будут в понедельник. Слова курса и фразы, которые уже переводили, открываются как раньше.`

const MAX_WORD = 60
const MAX_PHRASE = 600
const MAX_CONTEXT = 300

async function saveRows(rows: CacheRow[]) {
  // Ошибка записи в кэш не должна ломать ответ ученику — перевод уже есть.
  try {
    await createAdminClient().from('translation_cache').upsert(rows, { onConflict: 'key', ignoreDuplicates: true })
  } catch {
    // нет ключа/сети до БД — просто не закэшировали
  }
}

// Разборы той же словоформы в других фразах, свежие первыми. Из них: словарь по
// словоформе (consensusOf, lib/tr-text.ts) и запасной ответ, когда модель
// недоступна (лимит недели, 503, таймаут). Ученику нужен результат, а не
// источник: отдаём как кэш.
async function cachedByWord(supabase: SupabaseClient, kind: TranslateKind, normalized: string, limit = 12): Promise<CachedTranslation[]> {
  if (kind !== 'word') return []
  const prefix = `word|${normalized}|`.replace(/[%_\\]/g, (c) => `\\${c}`)
  const { data } = await supabase.from('translation_cache').select('result').like('key', `${prefix}%`).order('created_at', { ascending: false }).limit(limit)
  return (data ?? []).map((r) => r.result as CachedTranslation)
}

// Шаги 1-2а: словарь курса, общий кэш, словарь по словоформе. Без модели,
// поэтому годятся и гостю (с клиентом service-role: кэш читают только вошедшие).
async function translateWithoutModel(
  db: SupabaseClient,
  kind: TranslateKind,
  text: string,
  context: string,
  normalized: string,
): Promise<{ hit: TranslateResult | null; byWord: CachedTranslation[]; key: string }> {
  // 1. Словарь курса. PostgreSQL lower()/ilike не знают турецкого I/ı —
  //    поэтому ilike только сужает выборку, окончательное сравнение — в JS
  //    по турецкой нормализации.
  const pattern = normalized.replace(/[%_\\]/g, (c) => `\\${c}`)
  const { data: dictRows } = await db.from('vocabulary').select('turkish, russian').ilike('turkish', pattern).limit(5)
  const dictHit = (dictRows ?? []).find((r) => normalizeTr(r.turkish) === normalized)
  const key = cacheKey(kind, text, context)
  if (dictHit) {
    return { hit: { kind, source: 'dictionary', translation: dictHit.russian, lemma: null, lemmaTranslation: null, breakdown: [] }, byWord: [], key }
  }

  // 2. Общий кэш.
  const { data: cached } = await db.from('translation_cache').select('result').eq('key', key).maybeSingle()
  if (cached?.result) return { hit: { ...(cached.result as CachedTranslation), source: 'cache' }, byWord: [], key }

  // 2а. Словарь по словоформе: разборы того же слова в других фразах сходятся
  //     в начальной форме и переводе (решение пользователя 03.10) — без модели.
  //     Результат запоминаем под точным ключом, дальше он находится сразу.
  const byWord = await cachedByWord(db, kind, normalized)
  const agreed = consensusOf(byWord)
  if (agreed) {
    await saveRows([{ key, kind, source: text, context, result: agreed }])
    return { hit: { ...agreed, source: 'cache' }, byWord, key }
  }
  return { hit: null, byWord, key }
}

export async function translateTurkish(input: { kind: TranslateKind; text: string; context?: string }): Promise<TranslateResult> {
  const kind: TranslateKind = input.kind === 'phrase' ? 'phrase' : 'word'
  const text = input.text.trim().slice(0, kind === 'word' ? MAX_WORD : MAX_PHRASE)
  const context = (input.context ?? '').trim().slice(0, MAX_CONTEXT) || text
  if (!text || !hasTurkish(text)) throw new Error('Нечего переводить')

  const normalized = normalizeTr(text)

  const supabase = await createClient()
  const { data: claims, error: claimsError } = await supabase.auth.getClaims()
  if (claimsError || !claims?.claims) {
    // Гость в уроке без входа (решение владельца 17.10.2026): словарь и кэш
    // бесплатно, с потолком на IP; модель только после входа.
    const none: TranslateResult = { kind, source: 'guest', translation: '', lemma: null, lemmaTranslation: null, breakdown: [] }
    const ip = (await headers()).get('x-forwarded-for')?.split(',')[0].trim() ?? null
    if (!guestCacheIpAllowed(ip)) return none
    const { hit, byWord } = await translateWithoutModel(createAdminClient(), kind, text, context, normalized)
    return hit ?? (byWord[0] ? { ...byWord[0], source: 'cache' } : none)
  }

  const { hit, byWord, key } = await translateWithoutModel(supabase, kind, text, context, normalized)
  if (hit) return hit

  // 3. Модель — только в пределах недельного лимита бесплатного тарифа.
  const userId = claims.claims.sub
  const device = await deviceId(true)
  const usage = await getAiUsage(supabase, userId, 'translate', device)
  if (usageExhausted(usage)) {
    const other = byWord[0]
    if (other) return { ...other, source: 'cache' }
    return { kind, source: 'limit', translation: '', lemma: null, lemmaTranslation: null, breakdown: [], notice: LIMIT_NOTICE(usage.limit!) }
  }

  try {
    if (kind === 'phrase') {
      const p = await getPhraseTranslation({ text })
      await recordAiUsage(userId, 'translate', device)
      const result: CachedTranslation = { kind, translation: p.translation, lemma: null, lemmaTranslation: null, breakdown: [] }
      await saveRows([{ key, kind, source: text, context: '', result }])
      return { ...result, source: 'ai' }
    }

    // Слово: сразу всё предложение, если в нём больше одного турецкого слова.
    const wordsInSentence = segmentText(context).filter((s) => s.word).length
    if (wordsInSentence > 1) {
      try {
        const rows = glossToCacheRows(context, await getSentenceGloss({ sentence: context }))
        await recordAiUsage(userId, 'translate', device)
        await saveRows(rows)
        const hit = rows.find((r) => r.key === key)
        if (hit) return { ...hit.result, source: 'ai' }
      } catch (e) {
        // Сервис перегружен (503) или не ответил вовремя — второй запрос за
        // одним словом только удвоит ожидание, сразу отдаём ошибку.
        const msg = e instanceof Error ? e.message : String(e)
        if (/503|too busy|TimeoutError|aborted/i.test(msg) || (e instanceof Error && e.name === 'TimeoutError')) throw e
        // разбор фразы не удался по другой причине — ниже переведём одно слово
      }
    }

    const w = await getWordTranslation({ word: text, sentence: context })
    await recordAiUsage(userId, 'translate', device)
    const result: CachedTranslation = { kind, translation: w.translation, lemma: w.lemma, lemmaTranslation: w.lemmaTranslation, breakdown: w.breakdown }
    await saveRows([{ key, kind, source: text, context, result }])
    return { ...result, source: 'ai' }
  } catch (e) {
    // Модель не ответила (503, таймаут): разбор того же слова из другой фразы, если он есть.
    const other = byWord[0]
    if (other) return { ...other, source: 'cache' }
    throw e
  }
}

// «Как сказать?» — удержание плавающей кнопки (components/word-lookup.tsx):
// русское слово/фраза голосом → турецкий. Порядок тот же: словарь курса →
// общий кэш → DeepSeek. В недельный лимит НЕ входит (решение пользователя
// 2026-09-24: подсказка посреди разговора — удобство, а не AI-функция), но
// от накрутки стоит суточный потолок на пользователя. Счётчик в памяти
// процесса: при перезапуске контейнера обнуляется — для защиты от скрипта
// этого хватает, миграция ради него не нужна.
export type RuLookupResult =
  | ({ status: 'ok'; query: string; source: 'dictionary' | 'cache' | 'ai' } & RuLookup)
  | { status: 'error'; notice: string }

const MAX_LOOKUP = 80
const LOOKUP_DAILY_CAP = 150
const lookupCounts = new Map<string, { day: string; n: number }>()

function takeLookupSlot(userId: string): boolean {
  const day = new Date().toISOString().slice(0, 10)
  const cur = lookupCounts.get(userId)
  const n = cur && cur.day === day ? cur.n : 0
  if (n >= LOOKUP_DAILY_CAP) return false
  lookupCounts.set(userId, { day, n: n + 1 })
  return true
}

export async function lookupRussian(rawText: string): Promise<RuLookupResult> {
  const supabase = await createClient()
  const { data: claims, error: claimsError } = await supabase.auth.getClaims()
  if (claimsError || !claims?.claims) throw new Error('Unauthorized')

  const query = rawText.replace(/\s+/g, ' ').trim().replace(/[.!?…]+$/, '').slice(0, MAX_LOOKUP)
  const normalized = normalizeRu(query)
  if (!normalized) return { status: 'error', notice: 'Не расслышал - держите кнопку и скажите слово по-русски.' }

  // 1. Словарь курса. ilike только сужает выборку (ё/е, скобки, перечисления
  //    в поле russian) — окончательное сравнение в russianGlossMatches.
  const firstWord = normalized.split(' ')[0].replace(/е/g, '_').replace(/[%\\]/g, (c) => `\\${c}`)
  const { data: dictRows } = await supabase.from('vocabulary').select('turkish, russian').ilike('russian', `%${firstWord}%`).limit(40)
  const hits = [...new Set((dictRows ?? []).filter((r) => russianGlossMatches(r.russian, normalized)).map((r) => r.turkish.trim()))]
  if (hits.length > 0) {
    return {
      status: 'ok',
      query,
      source: 'dictionary',
      turkish: hits[0],
      alternatives: hits.slice(1, 3).map((turkish) => ({ turkish, noteRu: '' })),
    }
  }

  // 2. Общий кэш.
  const key = lookupCacheKey(query)
  const { data: cached } = await supabase.from('translation_cache').select('result').eq('key', key).maybeSingle()
  if (cached?.result) return { status: 'ok', query, source: 'cache', ...(cached.result as RuLookup) }

  // 3. Модель.
  if (!takeLookupSlot(claims.claims.sub)) {
    return { status: 'error', notice: 'На сегодня подсказок слишком много - продолжим завтра. Слова курса находятся как обычно.' }
  }
  try {
    const result = await getRuToTrLookup({ text: query })
    await saveRows([{ key, kind: normalized.includes(' ') ? 'phrase' : 'word', source: query, context: '', result: result as unknown as CachedTranslation }])
    return { status: 'ok', query, source: 'ai', ...result }
  } catch (e) {
    console.error('[lookup]', e instanceof Error ? e.message : e)
    return { status: 'error', notice: 'Сервис перевода сейчас не ответил - попробуйте ещё раз.' }
  }
}
